import { Link, NavLink, useNavigate } from "react-router-dom";
import { Phone, Menu, X, LogOut, LayoutDashboard, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Magnetic } from "@/motion";
import { useAuth } from "@/context/AuthContext";
import { HOTLINE } from "@/lib/api";
import { Button } from "@/components/ui/button";

const LINKS = [
  ["Find Healthcare", "/search", "nav-find-healthcare"],
  ["HealthAI", "/healthai", "nav-health-ai"],
  ["Diagnostic Lab", "/diagnostics", "nav-diagnostic-lab"],
  ["Wellness Mart", "/wellness-mart", "nav-wellness-mart"],
  ["Insurance", "/insurance", "nav-insurance"],
  ["Join as Doctor", "/join-doctor", "nav-join-doctor"],
];

export const Logo = ({ dark }) => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
    <img src="/logo.jpeg" alt="Apnicare" className="h-10 w-10 rounded-xl object-cover ring-1 ring-white/10" />
    <div className="leading-none">
      <div className={`font-display font-extrabold tracking-tight text-lg ${dark ? "text-white" : "text-[#06152D]"}`}>APNICARE</div>
      <div className={`text-[9px] tracking-[0.18em] uppercase ${dark ? "text-sky-300" : "text-[#0284C7]"}`}>built for care</div>
    </div>
  </Link>
);

export function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-40 navy text-white border-b border-white/10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Logo dark />
        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.map(([label, to, id]) => (
            <NavLink key={to} to={to} data-testid={id}
              className={({ isActive }) => `nav-link px-3 py-2 text-sm font-medium transition-colors ${isActive ? "active text-white" : "text-slate-300 hover:text-white"}`}>
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <a href={`tel:${HOTLINE}`} data-testid="nav-call-whatsapp-button" data-cursor="CALL"
            className="hidden md:flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs font-semibold hover:bg-white/15 transition-colors">
            <span className="relative flex h-2 w-2 pulse-dot"><span className="h-2 w-2 rounded-full bg-sky-400" /></span>
            <Phone size={13} /> {HOTLINE}
          </a>
          {user ? (
            <div className="hidden md:flex items-center gap-1">
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/10 rounded-full" data-testid="nav-dashboard-button" onClick={() => nav(user.role === "admin" ? "/admin" : "/dashboard")}>
                {user.role === "admin" ? <ShieldCheck size={16} /> : <LayoutDashboard size={16} />}
                <span className="ml-1.5">{user.role === "admin" ? "Admin" : "My Health"}</span>
              </Button>
              <Button size="sm" variant="ghost" className="text-slate-300 hover:bg-white/10 rounded-full" data-testid="nav-logout-button" onClick={logout}><LogOut size={16} /></Button>
            </div>
          ) : (
            <Magnetic className="hidden md:inline-block"><Button size="sm" data-testid="nav-sign-in-button" onClick={() => nav("/login")}
              className="rounded-full bg-white text-[#06152D] hover:bg-sky-100 font-semibold px-4">Sign In</Button></Magnetic>
          )}
          <button className="lg:hidden p-2 rounded-lg hover:bg-white/10" data-testid="nav-mobile-menu-button" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      <AnimatePresence>{open && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }} className="lg:hidden border-t border-white/10 bg-[#0A192F] px-4 py-3 flex flex-col gap-1" data-testid="nav-mobile-menu">
          {LINKS.map(([label, to, id]) => (
            <NavLink key={to} to={to} data-testid={`mobile-${id}`} onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm text-slate-200 hover:bg-white/10">{label}</NavLink>
          ))}
          {user ? (
            <><Link to={user.role === "admin" ? "/admin" : "/dashboard"} onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm text-sky-300">{user.role === "admin" ? "Admin Dashboard" : "My Health"}</Link>
              <button onClick={() => { logout(); setOpen(false); }} className="px-3 py-2.5 rounded-lg text-sm text-left text-slate-300">Sign out</button></>
          ) : <Link to="/login" data-testid="mobile-nav-sign-in" onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-lg text-sm font-semibold text-sky-300">Sign In / Get Started</Link>}
        </motion.div>
      )}</AnimatePresence>
    </header>
  );
}
